import { ChevronRightIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, XIcon } from '@heroicons/react/solid';
import { ConfigProvider, Drawer, InputRef, ThemeConfig, Tooltip } from 'antd';
import { Avatar } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import AppStoreGetIcon from 'assets/Icons/AppStoreGetIcon';
import LogoSvg from 'assets/Icons/orangeLogo';
import PlayStoreGetIcon from 'assets/Icons/PlayStoreGetIcon';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { Inter_Tight } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import LogoutButton from '@/components/Layout/ProductsHeader/LogoutButton';
import PersonalityValue from '@/components/Layout/ProductsHeader/PersonalityValue';
import SubscribeButton from '@/components/Layout/ProductsHeader/SubscribeButton';
import ViewProfile from '@/components/Layout/ProductsHeader/ViewProfile';

import FloatingCartButton from './FlottingButton';
import ProductDropdown from './ProductDropdown';
import GetOurAppModal from '../GetAppComp';
// import ButtonDefault, { ButtonVariants } from '../shared/basic/button';
import { ShouldRender } from '../shared/basic/ShouldRender';
import { getUserInitials } from '../shared/getInitials';
import { getUserName } from '../shared/getToken';
import ForgotPasswordModal from '../V2/ForgotPasswordModal';
import LoginModal from '../V2/LoginModal';
import SignUpModal from '../V2/SignUpModal';
import VerifyEmailModal from '../VerifyEmailModal';
import { authModalControl, tokenUpdate } from '../../atoms/authModalControl';
import { chatCount } from '../../atoms/chatCount';
import { hasNewBookAtom } from '../../atoms/hasNewBook';
import { localCart } from '../../atoms/LocalCart';
import { user } from '../../atoms/user';
import { verifyModal } from '../../atoms/verifyModal';
import appwriteService from '../../config/appwrite';
import { checkHasNewProduct } from '../../services/Library/Books';
import { User } from '../../utils/interfaces/User';
import { PreviewSchool, ZebraLearnLogo } from '../../../assets';
import BusinessHeader from '../../../assets/Icons/BusinessHeader';
import InfoCircleIcon from '../../../assets/Icons/InfoCircle';
import KeyIcon from '../../../assets/Icons/Key';
import LibraryIcon from '../../../assets/Icons/LibraryIcon';
import BookIcon from '../../../assets/Icons/NewHeaderIcons/BookIcon';
// import CoinHeader from '../../../assets/Icons/NewHeaderIcons/CoinHeader';
import ContactUs from '../../../assets/Icons/NewHeaderIcons/ContactUs';
import CourseIcon from '../../../assets/Icons/NewHeaderIcons/CourseIcon';
import LiveIcon from '../../../assets/Icons/NewHeaderIcons/LiveIcon';
import SchoolIcon from '../../../assets/Icons/NewHeaderIcons/SchoolIcon';
import PencilEdit from '../../../assets/Icons/PencilEdit';
import ProfileIcons from '../../../assets/Icons/ProfileIcons';
import ReceiptItem from '../../../assets/Icons/receipt-item';
// import SearchIconSVG from '../../../assets/Icons/SearchIconSVG';
import ShoppingCart from '../../../assets/Icons/shopping-cart';

interface HeaderLinkProps {
  text: string | ReactNode;
  route?: string;
  setOpen: (value: boolean) => void;
  isDrawer?: boolean;
  notRender?: boolean;
  index?: number;
  open?: boolean;
  notHaveDropDown?: boolean;
}

export interface RenderAvatarProps {
  containerStyle?: CSSProperties;
  wrapperStyle?: string;
  width?: number;
  redirect?: boolean;
  avatarStyle?: {
    fontSize: string;
    border: string;
  };
  userName?: string;
  avatarSrc?: string;
  gradientBg?: string;
  setShowDropDown?: (value: boolean) => void;
  onClose?: () => void;
}

const RenderAvatar = ({
  containerStyle,
  wrapperStyle,
  width,
  redirect,
  avatarStyle,
  userName,
  avatarSrc,
  gradientBg,
  setShowDropDown,
  onClose,
}: RenderAvatarProps) => {
  const router = useRouter();
  const avatarBackground = useMemo(
    () => (!avatarSrc ? gradientBg : undefined),
    [avatarSrc, gradientBg],
  );

  const avatarClass = useMemo(
    () => clsx(!avatarSrc ? 'bg-primary' : 'bg-bgColor'),
    [avatarSrc],
  );

  const shouldRenderEditIcon = useMemo(() => !!redirect, [redirect]);

  const handleClick = () => {
    if (redirect) {
      router.push('/profile');
      React.startTransition(() => {
        setShowDropDown && setShowDropDown(false);
        onClose && onClose();
      });
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{ width, height: width, ...containerStyle }}
      className={clsx(wrapperStyle, 'relative')}
    >
      <Avatar
        size={width}
        alt='profile-picture'
        style={{
          ...avatarStyle,
          background: avatarBackground,
        }}
        className={avatarClass}
        src={avatarSrc}
      >
        {!avatarSrc && !userName ? (
          <ProfileIcons width='8' />
        ) : (
          userName && getUserInitials(userName)
        )}
      </Avatar>
      {shouldRenderEditIcon && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            borderRadius: '50%',
            cursor: 'pointer',
          }}
        >
          <div className='bg-[#121212] translate-y-1 rounded-full flex justify-center items-center'>
            <PencilEdit width='20' fillBgColor='#121212' />
          </div>
        </div>
      )}
    </div>
  );
};
export interface RenderMenuSectionProps {
  items: {
    text: string;
    customSVG?: ReactNode;
    onClick?: () => void;
    link?: string;
    notHaveDropDown?: boolean;
  }[];
  isCenter?: boolean;
}

interface ProductsHeaderProps {
  isShadow?: boolean;
  links: {
    text: string | ReactNode;
    route?: string;
    notRender?: boolean;
    type?: string;
    notHaveDropDown?: boolean;
  }[];
  isLoggedIn?: boolean;
}

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
  },
};

const interTight = Inter_Tight({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

export enum HeaderIcons {
  // HOME = 'Home',
  BOOKS = 'Books',
  COURSES = 'Courses',
  SCHOOLS = 'Schools',
  LIVE = 'Live',
  CONTACT_US = 'Contact Us',
  BUSINESS = 'For Business',
}

export const ICON_MAPPING = {
  [HeaderIcons.BOOKS]: <BookIcon />,
  [HeaderIcons.COURSES]: <CourseIcon />,
  [HeaderIcons.SCHOOLS]: <SchoolIcon />,
  [HeaderIcons.LIVE]: <LiveIcon />,
  [HeaderIcons.CONTACT_US]: <ContactUs />,
  [HeaderIcons.BUSINESS]: <BusinessHeader />,
};
export const HeaderLink: React.FC<HeaderLinkProps> = ({
  text,
  route,
  setOpen,
  isDrawer = false,
  notRender = false,
  index = 0,
  open = false,
  notHaveDropDown = false,
}) => {
  const router = useRouter();
  const screens = useBreakpoint();
  const isSelected =
    route &&
    !router.pathname.includes('/product') &&
    (router.pathname === route ||
      (router.pathname.includes(route) && route !== '/'));

  const isHome = text !== 'Home';

  const linkRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected && linkRef.current) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });

      setTimeout(() => {
        const offset = 100;
        const rect = linkRef.current?.getBoundingClientRect();
        if (rect) {
          const scrollPosition = rect.left - offset;
          window.scrollBy({ left: scrollPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isSelected]);

  const content = (
    <div ref={linkRef} className='group w-full'>
      <div
        className={clsx(
          ' flex items-center cursor-pointer border   w-full',
          !isHome ? 'p-1.5 lg:p-2 rounded-full ' : ' rounded-[10px]',
          !isSelected
            ? 'border-none md:px-4 px-3.5 py-2 md:py-1.5 lg:py-2.5  bg-cartBgColor group-hover:bg-grayZl-900  text-textLightGray'
            : 'border-none md:pr-2.5 md:pl-2.5 px-3.5  py-2 md:py-1.5 lg:py-2.5 text-white bg-primary',
        )}
      >
        {!isHome ? (
          <motion.div
            key='icon'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {ICON_MAPPING[text as HeaderIcons]}
          </motion.div>
        ) : (
          <motion.div
            key='icon-text'
            className='flex items-center w-full gap-2 '
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <ShouldRender check={isHome}>
              <span className='text-sm whitespace-nowrap w-full font-medium'>
                {text}
              </span>
            </ShouldRender>
            <ShouldRender check={screens.lg && !notHaveDropDown}>
              <ChevronDownIcon className='w-4 h-4 font-medium transform transition-transform duration-300 group-hover:rotate-180' />
            </ShouldRender>
          </motion.div>
        )}
      </div>
    </div>
  );

  return (
    <ShouldRender check={!notRender}>
      <ShouldRender check={!isDrawer}>
        <AnimatePresence>
          <motion.div
            onClick={() => {
              if (!isDrawer) {
                route && router.push(route);
                setOpen && setOpen(false);
              }
            }}
            key={`${text}-${route}-${isSelected}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={clsx(
              'flex h-full items-center hover:cursor-pointer w-full text-center',
              isSelected ? 'text-primary' : 'text-gray-600',
            )}
          >
            {text === 'SCHOOLS' ? (
              <Tooltip
                title={
                  <div
                    onClick={() => {
                      route && router.push(route);
                    }}
                    className='flex justify-center cursor-pointer'
                  >
                    <Image src={PreviewSchool} alt='AuthorLarge' width={270} />
                  </div>
                }
              >
                {content}
              </Tooltip>
            ) : (
              content
            )}
          </motion.div>
        </AnimatePresence>
      </ShouldRender>
      <ShouldRender check={isDrawer && open}>
        <motion.div
          onClick={() => {
            if (!isDrawer) {
              route && router.push(route);
              setOpen && setOpen(false);
            }
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
            delay: 0.1 + index / 10,
          }}
          className={clsx(
            'flex h-full items-center hover:cursor-pointer',
            isSelected ? 'text-primary' : 'text-gray-600',
          )}
        >
          {text === 'SCHOOLS' ? (
            <Tooltip
              title={
                <div
                  onClick={() => {
                    route && router.push(route);
                  }}
                  className='flex justify-center cursor-pointer'
                >
                  <Image src={PreviewSchool} alt='AuthorLarge' width={270} />
                </div>
              }
            >
              {content}
            </Tooltip>
          ) : (
            content
          )}
        </motion.div>
      </ShouldRender>
    </ShouldRender>
  );
};

const ProductsHeader: React.FC<ProductsHeaderProps> = ({
  isShadow = false,
  links,
}) => {
  const router = useRouter();
  const [showSearch, setShowSearch] = useState(false);
  const [userData] = useRecoilState<User | undefined>(user);
  const [visible, setVisible] = useRecoilState(authModalControl);
  // const [showDropDown, setShowDropDown] = useState(false);
  const [, setShowDropDown] = useState(false);
  // const [searchKeyword, setSearchKeyword] = useState<string>();
  const [, setSearchKeyword] = useState<string>();
  // const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [, setLoggedIn] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>();
  const [visibleGetAppModal, setVisibleGetAppModal] = useState(false);
  const [visibleVerifyEmailModal, setVisibleVerifyEmailModal] =
    useRecoilState(verifyModal);

  const [openDropDown, setOpenDropDown] = useState(false);

  // const [isBannerVisibleValue, setIsBannerVisibleValue] = useRecoilState<
  //   boolean | null
  // >(isBannerVisible);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const bannerRoutes = [
    '/',
    '/books',
    '/courses',
    '/schools',
    '/live',
    '/contact',
  ];

  const isMatchingRoute = useMemo(() => {
    return bannerRoutes.some(
      (bannerRoute) =>
        router.pathname === bannerRoute ||
        router.pathname === `${bannerRoute}/`,
    );
  }, [router.pathname, bannerRoutes]);

  const chatCountValue = useRecoilValue(chatCount);

  const [open, setOpen] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [androidRedirectUrl, setAndroidRedirectUrl] = useState<string | null>(
    null,
  );
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const screens = useBreakpoint();
  const localCartCount = useRecoilValue(localCart);
  const inputRef = useRef<InputRef | null>(null);
  const isSchoolsPage = router.pathname.startsWith(
    '/schools/[schoolId]/[postId]',
  );
  const isCheckoutPage = !router.asPath.includes('/checkout');
  const isCartPage = router.asPath.includes('/cart');
  // const isVerified = useRef(false);
  const [hasNewBook, setHasNewBook] = useRecoilState<boolean>(hasNewBookAtom);
  const isPdf = router.asPath.startsWith('/pdf/');
  const validPaths = Object.freeze([
    '/',
    '/books/',
    '/books/shop',
    '/books/shop/',
    '/books/shop/[category]',
    '/courses/shop',
    '/courses/shop/',
    '/courses/shop/[category]',
    '/books/[bookId]',
    '/schools/[schoolId]',
    '/schools/',
    '/live/',
    '/courses/',
    '/about_us/',
    '/contact/',
    '/library/',
    '/broadcast-message/',
  ]);

  const validPathSearch = [
    '/',
    '/books/',
    '/books',
    '/books/shop',
    '/books/shop/[category]',
    '/courses/shop',
    '/books/shop/',
    '/courses/shop/',
    '/courses/shop/[category]',
    '/schools/',
    '/schools',
    '/live/',
    '/live',
  ];

  const validPathSearchNav = [
    '/',
    '/books/',
    '/books',
    '/books/shop',
    '/books/shop/[category]',
    '/books/shop/',
    '/courses/shop',
    '/courses/shop/',
    '/courses/shop/[category]',
    '/schools/',
    '/schools',
    '/live/',
    '/live',
    '/courses/',
    '/courses',
    '/contact/',
    '/contact',
  ];

  const keyWord = router?.query?.search || '';
  // const environment = process.env.NEXT_PUBLIC_APP_ENV;
  const fullName = userData?.name || userName || '';
  const firstTwoWords = fullName.split(' ').slice(0, 2).join(' ');
  const displayName =
    firstTwoWords.length > 15
      ? `${firstTwoWords.slice(0, 20)}...`
      : firstTwoWords;
  const isShopPage =
    !router.pathname.includes('/books/shop') &&
    !router.pathname.includes('/courses/shop');

  // const dropdownRef = useRef<HTMLDivElement | null>(null);

  const avatarSrc =
    userData?.avatar &&
    `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${userData?.avatar}`;

  // const handleRemoveBanner = () => {
  //   setIsBannerVisibleValue(false);

  //   setTimeout(() => {
  //     setIsBannerVisibleValue(null);
  //   }, 2000);
  // };
  const cleanedPath = router.pathname.trim();

  const shouldRenderLinks = validPaths.some((path) =>
    cleanedPath.startsWith(path),
  );

  const onCancelCloseModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      signUp: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseLoginModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      login: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseForgotPasswordModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
  };

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      const user_name = await getUserName();
      user_name && setUserName(user_name);
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  useEffect(() => {
    if (showSearch && shouldRenderLinks && isCheckoutPage && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch, shouldRenderLinks, isCheckoutPage]);

  // const sendVerificationLink = async () => {
  //   try {
  //     await appwriteService.sendVerificationEmail();
  //     setVisibleVerifyEmailModal(true);
  //   } catch (error: any) {
  //     notify(formateErrorAppwriteObject(error));
  //   }
  // };

  useEffect(() => {
    if (screens.md) {
      onClose();
    }
  }, [screens]);

  useEffect(() => {
    setShowSearch(false);
  }, [router.pathname]);

  const renderMenuSection = ({
    items,
    isCenter = false,
  }: RenderMenuSectionProps) => (
    <div className={twMerge(interTight.className, 'px-5')}>
      <div className='flex flex-col font-medium rounded-[10px] border border-grayZl-900 overflow-hidden gap-px bg-grayZl-900'>
        {items.map((item, index) => {
          const isActive = item.link
            ? router.pathname.includes(item.link)
            : false;

          return (
            <button
              key={`${item.text}-${index}`}
              onClick={() => {
                item.onClick && item.onClick();
                if (item.text !== 'More Settings') {
                  setShowDropDown(false);
                }
              }}
              className={`
                ${!isCenter ? 'justify-start gap-x-3.5' : 'justify-center gap-x-2'}
                 bg-[#000000] py-2.5 md:py-2.5 px-3
                text-textGrayColor hover:text-primary flex items-center
              `}
            >
              <div className='flex justify-between items-center w-full'>
                <span className='flex gap-x-3 items-center text-white'>
                  <div
                    className={clsx(
                      isActive ? 'text-white' : 'text-[#9F9EA6]',
                      'flex items-center',
                    )}
                  >
                    {item.customSVG}
                  </div>

                  <span className='text-xs/5 text-white font-medium'>
                    {item.text}
                  </span>
                </span>

                <div className='flex justify-center items-center gap-x-3'>
                  <ShouldRender
                    check={
                      (chatCountValue && item.text === 'Message') ||
                      (hasNewBook && item.text === 'Library')
                    }
                  >
                    <span className='w-1.5 h-1.5 rounded-full bg-primary'></span>
                  </ShouldRender>
                  <span className='flex justify-center items-center rounded-full'>
                    <ChevronRightIcon className='h-4 text-white' />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const gradientBg = 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)';

  const onClose = () => {
    setOpen(false);
  };
  // const dropdownMenu = (
  //   <div
  //     ref={dropdownRef}
  //     className={clsx(
  //       interTight.className,
  //       'custom-dropdown-menu bg-grayZl-950  rounded-xl text-fadedWhite customScrollBar',
  //     )}
  //   >
  //     <div className='overflow-y-auto flex flex-col gap-4'>
  //       <ViewProfile
  //         userName={userName}
  //         avatarSrc={avatarSrc}
  //         gradientBg={gradientBg}
  //         RenderAvatar={RenderAvatar}
  //         setShowDropDown={setShowDropDown}
  //         onClose={onClose}
  //         setOpen={setOpen}
  //         displayName={displayName}
  //       />
  //       {/* <SubscribeButton setShowDropDown={setShowDropDown} setOpen={setOpen} /> */}
  //       <PersonalityValue onClose={onClose} setShowDropDown={setShowDropDown} />
  //       {/* <CompleteProfile
  //         sendVerificationLink={sendVerificationLink}
  //         setShowDropDown={setShowDropDown}
  //         onClose={onClose}
  //       /> */}
  //       {/* <ReferEarn setShowDropDown={setShowDropDown} onClose={onClose} /> */}
  //       {renderMenuSection({
  //         items: [
  //           // ...(isSubscriptionActive(userData)
  //           //   ? [
  //           //       {
  //           //         text: 'Subscription',
  //           //         link: 'subscription',
  //           //         customSVG: <CrownOutlineIcon width={20} height={20} />,
  //           //         onClick: () => {
  //           //           router.push('/subscription/');
  //           //           onClose();
  //           //         },
  //           //       },
  //           //     ]
  //           //   : []),
  //           {
  //             text: 'Library',
  //             link: 'library',
  //             customSVG: <LibraryIcon width={20} height={20} />,
  //             onClick: () => {
  //               router.push('/library/');
  //               onClose();
  //             },
  //           },
  //           {
  //             text: 'Cart',
  //             link: 'cart',
  //             customSVG: <ShoppingCart width={20} />,
  //             onClick: () => {
  //               router.push('/cart');
  //               onClose();
  //             },
  //           },
  //           // {
  //           //   text: 'Rewards',
  //           //   link: 'reward-store',
  //           //   customSVG: <RewardIcon width={20} height={20} />,
  //           //   onClick: () => {
  //           //     router.push('/reward-store');
  //           //     onClose();
  //           //   },
  //           // },
  //           {
  //             text: 'My Orders',
  //             link: 'my-orders',
  //             customSVG: <ReceiptItem width={20} height={20} />,
  //             onClick: () => {
  //               router.push('/my-orders/');
  //               onClose();
  //             },
  //           },
  //           // {
  //           //   text: 'Message',
  //           //   link: 'broadcast-message',
  //           //   customSVG: <ChatIcon width={20} height={20} />,
  //           //   onClick: () => {
  //           //     router.push('/broadcast-message');
  //           //     onClose();
  //           //   },
  //           // },
  //         ],
  //       })}
  //       <LogoutButton setShowDropDown={setShowDropDown} />
  //     </div>
  //     {/* <ShouldRender check={screens.md}>
  //       <div className='border-b  border-dividerColor pt-2.5' />
  //     </ShouldRender> */}
  //   </div>
  // );

  // const showDrawer = () => {
  //   setOpen(true);
  // };

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  // const verifyUserEmail = async () => {
  //   try {
  //     await appwriteService.verifyVerificationEmail(
  //       router.query.userId as string,
  //       router.query.secret as string,
  //     );
  //     const data = await getUserProfile();
  //     setUserData(data?.data);
  //     router.replace(router.pathname, undefined, { shallow: true });
  //   } catch (error: any) {
  //     notify(formateErrorObject(error), 'error');
  //   }
  // };

  // useEffect(() => {
  //   if (!isVerified.current && router.query.userId && router.query.secret) {
  //     verifyUserEmail();
  //     isVerified.current = true;
  //   }
  // }, [router.query.userId, router.query.secret]);

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const newSearchKeyword = e.target.value;
  //   setSearchKeyword(newSearchKeyword);
  //   Cookies.set('search', newSearchKeyword);

  //   router.push(
  //     {
  //       pathname: router.pathname,
  //       query: { ...router.query, search: newSearchKeyword },
  //     },
  //     undefined,
  //     { shallow: true },
  //   );
  // };

  // const useAppButton = (
  //   <div className={interTight.className}>
  //     <Button
  //       onClick={() => {
  //         if (typeof window !== 'undefined') {
  //           window.open('https://zbrlrn.com/download-zl-app');
  //         }
  //         const eventParams: EventDetails = {
  //           event_name: 'download_app',
  //           source: 'global',
  //           source_type: 'global',
  //           source_id: null,
  //           sub_source: null,
  //           sub_source_id: null,
  //           unit: 'boolean',
  //           value: 1,
  //           meta: {
  //             click_source: 'app_popup',
  //           },
  //         };

  //         logEvent({
  //           eventDetails: eventParams,
  //           userData: userData,
  //           pathname: router.pathname,
  //         });
  //         if (typeof mixpanel !== 'undefined' && environment === 'production') {
  //           mixpanel.track(eventParams.event_name as string, eventParams);
  //         }
  //       }}
  //       className='border-primary border-1 bg-transparent border-opacity-50 md:border-[2px] text-[10px] xxxs:text-xs md:text-base px-2 md:px-4 hover:text-white hover:border-[#737373] text-primary h-7 xs:h-8 md:h-[38px] rounded-lg  font-semibold'
  //     >
  //       Use App
  //     </Button>
  //   </div>
  // );

  // const desktopUseApp = (
  //   <div className={interTight.className}>
  //     <ButtonDefault
  //       size={0}
  //       variant={ButtonVariants.UNSTYLED}
  //       noRadius={true}
  //       className=' border-b border-primary text-sm text-primary font-semibold'
  //       onClick={() => {
  //         if (typeof window !== 'undefined') {
  //           window.open('https://zbrlrn.com/download-zl-app');
  //         }
  //         const eventParams: EventDetails = {
  //           event_name: 'download_app',
  //           source: 'global',
  //           source_type: 'global',
  //           source_id: null,
  //           sub_source: null,
  //           sub_source_id: null,
  //           unit: 'boolean',
  //           value: 1,
  //           meta: {
  //             click_source: 'app_popup',
  //           },
  //         };

  //         logEvent({
  //           eventDetails: eventParams,
  //           userData: userData,
  //           pathname: router.pathname,
  //         });
  //         if (typeof mixpanel !== 'undefined' && environment === 'production') {
  //           mixpanel.track(eventParams.event_name as string, eventParams);
  //         }
  //       }}
  //     >
  //       Use App <ChevronRightIcon className='w-3 h-3' />
  //     </ButtonDefault>
  //   </div>
  // );
  const initialize = async () => {
    const cookieValue = Cookies.get('hasNewBookInLibrary');

    if (cookieValue !== undefined) {
      setHasNewBook(cookieValue === 'true');
      return;
    }

    try {
      const { data } = await checkHasNewProduct();

      const hasNew = data?.hasNewProduct === true;

      Cookies.set('hasNewBookInLibrary', hasNew.toString(), { expires: 1 });
      setHasNewBook(hasNew);
    } catch (error) {
      console.error('Failed to check for new product:', error);

      Cookies.set('hasNewBookInLibrary', 'false', { expires: 0.05 });
      setHasNewBook(false);
    }
  };

  useEffect(() => {
    initialize();
  }, [router.pathname]);

  // const mobileBanner = (
  //   <motion.div
  //     className='bg-cartBgColor flex justify-between items-center h-16 px-4  -translate-y-2 w-full z-40'
  //     initial={{ x: 0 }}
  //     animate={{ x: 0 }}
  //     exit={{ x: '100%' }}
  //     transition={{ duration: 0.3 }}
  //   >
  //     <div className='flex justify-between w-full items-center '>
  //       <div className='flex gap-x-4 items-center justify-start'>
  //         <button
  //           type='button'
  //           className='px-0 flex items-center rounded-full'
  //           // onClick={handleRemoveBanner}
  //           aria-label='Close banner'
  //         >
  //           <XIcon className='w-5 h-5' />
  //         </button>
  //         <div className='flex flex-col justify-center'>
  //           <span className='font-semibold text-sm'>ZebraLearn App</span>
  //           <span className='text-xs font-medium text-textLightGray'>
  //             Install for a better experience
  //           </span>
  //         </div>
  //       </div>
  //       <div className='mt-1'>{useAppButton}</div>
  //     </div>
  //   </motion.div>
  // );
  // const desktopBanner = (
  //   <motion.div
  //     className='bg-cartBgColor flex justify-between items-center h-12 px-4 w-full z-50'
  //     initial={{ y: 100, opacity: 0 }}
  //     animate={{ y: 0, opacity: 1 }}
  //     exit={{ y: -100, opacity: 0 }}
  //     transition={{ duration: 0.3 }}
  //   >
  //     <div className='flex justify-between w-full z-50 items-center'>
  //       <div className='flex gap-x-4 items-center justify-center w-full'>
  //         <span className='text-sm text-white font-light flex items-center gap-x-3.5'>
  //           Get Access to 10 Free Books on ZebraLearn App {desktopUseApp}
  //         </span>
  //       </div>
  //       <button
  //         // onClick={handleRemoveBanner}
  //         className='bg-[#0000004D] rounded-full p-1.5 text-white flex justify-center items-center'
  //       >
  //         <XIcon className='w-4 h-4' />
  //       </button>
  //     </div>
  //   </motion.div>
  // );

  // const shouldRenderBanner =
  //   isBannerVisibleValue === true &&
  //   (validPathSearchNav.includes(router.pathname) ||
  //     router.asPath.includes('/pdf'));

  // useEffect(() => {
  //   if (screens.md) {
  //     setIsBannerVisibleValue(false);
  //   }
  // }, [screens]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);

    const defaultParams: Record<string, string> = {
      utm_source: 'zebralearn',
      utm_medium: 'website',
      utm_campaign: 'download_app',
    };

    const finalParams = new URLSearchParams();

    urlParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!finalParams.has(key)) {
        finalParams.set(key, value);
      }
    });

    const encodedReferrer = encodeURIComponent(finalParams.toString());
    const androidUrl = `https://play.google.com/store/apps/details?id=com.zebralearn.reader&referrer=${encodedReferrer}`;

    setAndroidRedirectUrl(androidUrl);
  }, []);

  // const isPdfPage = router.asPath.includes('/pdf');
  // const isDesktop = screens.md;
  return (
    <>
      <ConfigProvider theme={config}>
        <ShouldRender check={false}>
          {/* check={shouldRenderBanner}> */}
          <></>
          {/* <AnimatePresence mode='wait'>
            <div className='relative z-40'>
              {isPdfPage ? (
                isDesktop ? (
                  desktopBanner
                ) : (
                  mobileBanner
                )
              ) : (
                <Affix offsetTop={0} style={{ zIndex: 9999 }}>
                  {isDesktop ? desktopBanner : mobileBanner}
                </Affix>
              )}
            </div>
          </AnimatePresence> */}
        </ShouldRender>
        <div className='flex flex-col'>
          <nav
            className={clsx(
              'flex py-2 z-40 px-4 md:px-5 lg:px-6 lg:gap-x-0 2xl:px-12 items-center bg-bgColor border-borderDarkGray shadow-md justify-between w-full h-14 lg:min-h-16 top-0 transition-all duration-300',
              (!isMatchingRoute || router.asPath.includes('/pdf')) &&
                'bg-bgColor border-borderDarkGray shadow-md',

              !isSchoolsPage && !isPdf && 'fixed',
              // isBannerVisibleValue &&
              !isCartPage &&
                isCheckoutPage &&
                validPathSearchNav.some((path) => path === router.pathname)
                ? // ? `mt-12 md:mt-0 `
                  `mt-auto md:mt-0 `
                : 'mt-0 -translate-y-0',
              !isShadow ? 'shadow-md' : '',
            )}
          >
            <div className='flex gap-x-1.5 w-full  justify-start items-center'>
              <span
                onClick={() => router.push('/')}
                className={clsx(
                  'cursor-pointer flex items-center justify-start xxxs:w-36  h-3 relative',
                )}
              >
                <LogoSvg className=' xxxs:mr-3  ' />
              </span>

              <span className='h-full md:flex relative hidden gap-x-1 items-center w-full'>
                <ShouldRender
                  check={
                    shouldRenderLinks &&
                    screens.md &&
                    isCheckoutPage &&
                    !isCartPage
                  }
                >
                  {links.map((link, index) => (
                    <div
                      key={`${link.route}-${index}`}
                      className='relative inline-block w-full lg:w-fit'
                      onMouseEnter={() => {
                        if (timeoutIdRef.current)
                          clearTimeout(timeoutIdRef.current);
                        setHoveredIndex(index);
                        setOpenDropDown(true);
                      }}
                      onMouseLeave={() => {
                        timeoutIdRef.current = setTimeout(() => {
                          setOpenDropDown(false);
                          setHoveredIndex(null);
                        }, 100);
                      }}
                      onClick={() => {
                        if (screens.lg) {
                          setHoveredIndex(null);
                          setOpenDropDown(false);
                        }
                      }}
                    >
                      <HeaderLink
                        text={link.text}
                        index={index}
                        open={openDropDown}
                        route={link.route}
                        notHaveDropDown={link?.notHaveDropDown || false}
                        setOpen={setOpen}
                      />

                      {openDropDown &&
                        hoveredIndex === index &&
                        link.type &&
                        screens.lg && (
                          <div
                            onMouseEnter={() => {
                              if (timeoutIdRef.current)
                                clearTimeout(timeoutIdRef.current);
                            }}
                            onMouseLeave={() => {
                              setOpenDropDown(false);
                              setHoveredIndex(null);
                            }}
                            className='absolute left-0 top-full z-50'
                          >
                            <ProductDropdown
                              open={openDropDown}
                              setOpen={setOpenDropDown}
                              type={link.type}
                            />
                          </div>
                        )}
                    </div>
                  ))}
                </ShouldRender>
              </span>
            </div>

            <div className='flex gap-x-1 xxxs:gap-x-1.5 md:gap-x-2 w-full items-center justify-end '>
              <ShouldRender check={screens.lg}>
                {/* <ShouldRender
                  check={
                    !showSearch &&
                    isShopPage &&
                    validPathSearch.includes(router.pathname)
                  }
                >
                  <motion.div
                    key='search-icon'
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      onClick={() => {
                        setShowSearch(!showSearch);
                      }}
                      className='border-[1.5px] flex cursor-pointer justify-center items-center border-[#282829] text-white rounded-full p-[9.5px]'
                    >
                      <SearchIcon />
                    </div>
                  </motion.div>
                </ShouldRender> */}

                <ShouldRender
                  check={
                    !showSearch && isShopPage && !isCartPage && isCheckoutPage
                  }
                >
                  <></>
                  {/* <motion.div
                    key='navbar'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <AnimatedNavbar />
                  </motion.div> */}
                </ShouldRender>

                {/* <ShouldRender check={shouldRenderLinks && isCheckoutPage}>
                  <ShouldRender
                    check={
                      showSearch && validPathSearch.includes(router.pathname)
                    }
                  >
                    <motion.span
                      className='flex justify-center customInput w-full'
                      key='search-input'
                      initial={{ opacity: 0, width: '60%', scale: 0.95 }}
                      animate={{ opacity: 1, width: '100%', scale: 1 }}
                      exit={{ opacity: 0, width: '60%', scale: 0.95 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 25,
                        duration: 0.3,
                      }}
                    >
                      <Input
                        size='small'
                        ref={inputRef}
                        placeholder='Search books, courses, schools and live'
                        className='text-sm rounded-full focus-within:bg-bgColor border-[1.5px] placeholder:text-textDarkGrayColor py-1 px-3 text-white h-10 lg:h-10 bg-bgColor customInputBlack makeBgBlack'
                        value={searchKeyword}
                        style={{
                          backgroundColor: '#000000',
                        }}
                        onChange={handleInputChange}
                        prefix={
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                          >
                            <SearchIconSVG width='20' />
                          </motion.div>
                        }
                        suffix={
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <XIcon
                              onClick={() => {
                                Cookies.set('search', '');
                                setSearchKeyword('');
                                const { search, ...restQuery } = router.query;
                                router.push(
                                  {
                                    pathname: router.pathname,
                                    query: restQuery,
                                  },
                                  undefined,
                                  { shallow: true },
                                );
                                setShowSearch(false);
                              }}
                              className='w-5 h-5 pr-1 text-[#A6A6A6] cursor-pointer'
                            />
                          </motion.div>
                        }
                      />
                    </motion.span>
                  </ShouldRender>
                </ShouldRender> */}
              </ShouldRender>

              {/* <AnimatePresence mode='wait'>
                <ShouldRender
                  check={userData && userData?.features?.length > 0}
                >
                  <motion.div
                    key='key-button'
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 20,
                    }}
                  >
                    <ButtonDefault
                      onClick={() => router.push('/gems-transactions')}
                      className='hover:bg-grayZl-900 bg-cartBgColor md:pl-3 pl-[9px] md:pr-3.5 pr-[11px] md:py-[9px] py-[5px] gap-1 rounded-full'
                      variant={ButtonVariants.UNSTYLED}
                      size={0}
                      roundedFull
                      noRadius
                    >
                      <div className='font-medium text-xs leading-[22px] flex gap-1 rounded-full'>
                        <KeyIcon2 className='w-5 h-5' />
                        {keyCount}
                      </div>
                    </ButtonDefault>
                  </motion.div>
                </ShouldRender>
              </AnimatePresence> */}

              {/* <ShouldRender
                check={
                  shouldRenderLinks &&
                  router.asPath !== '/contact/' &&
                  !router.asPath.includes('/broadcast-message/') &&
                  !router.pathname.includes('/books/[bookId]') &&
                  validPathSearch.includes(router.pathname)
                }
              >
                <ShouldRender check={loggedIn}>
                  <div
                    onClick={() => router.push('/rewards')}
                    className={`rounded-full border  hover:bg-grayZl-900 bg-cartBgColor border-none pr-2 lG:pr-3  cursor-pointer flex justify-center items-center `}
                  >
                    <span className='w-6 h-6 lg:w-8 lg:h-8 flex justify-center items-center m-1 pl-2'>
                      <CoinHeader />
                    </span>
                    <p
                      className={clsx(
                        'font-medium flex justify-center text-xs md:text-sm items-center my-1  min-w-6 text-[#F9F9FA]',
                        userData?.balance && userData.balance > 100
                          ? 'mr-2'
                          : 'mr-1',
                      )}
                    >
                      {userData?.balance || 0}
                    </p>
                  </div>
                </ShouldRender>
              </ShouldRender> */}

              {/* <ShouldRender check={loggedIn}> */}
              {/* <ShouldRender
                  check={
                    !(
                      shouldRenderLinks &&
                      router.asPath !== '/contact/' &&
                      !router.asPath.includes('/broadcast-message/') &&
                      !router.pathname.includes('/books/[bookId]') &&
                      validPathSearch.includes(router.pathname)
                    ) &&
                    !isCartPage &&
                    isCheckoutPage
                  }
                >
                  <button
                    onClick={() => router.push('/rewards')}
                    className={`rounded-full border  hover:bg-grayZl-900 bg-cartBgColor border-none  cursor-pointer flex justify-center items-center `}
                  >
                    <span className='w-6 h-6 md:w-8 md:h-8 flex justify-center items-center m-1'>
                      <CoinHeader />
                    </span>
                    <p
                      className={clsx(
                        'font-semibold flex justify-center text-xs md:text-sm items-center my-1  min-w-6 text-[#F9F9FA]',
                        userData?.balance && userData.balance > 100
                          ? 'mr-2'
                          : 'mr-1',
                      )}
                    >
                      {userData?.balance || 0}
                    </p>
                  </button>
                </ShouldRender> */}
              {/* <Badge
                  dot
                  color='#FF5612'
                  count={chatCountValue || hasNewBook ? 1 : 0}
                  className='customDot'
                  style={{ color: 'white', fontSize: '11px' }}
                >
                  <Dropdown
                    overlay={dropdownMenu}
                    trigger={['click']}
                    open={screens.md ? showDropDown : false}
                    onOpenChange={(visible) =>
                      screens.md ? setShowDropDown(visible) : showDrawer()
                    }
                    overlayStyle={{
                      width: '390px',
                    }}
                  >
                    <div
                      ref={dropdownRef}
                      onClick={(e) => {
                        e.preventDefault();

                        screens.md
                          ? setShowDropDown(!showDropDown)
                          : showDrawer();
                      }}
                    >
                      <Space>
                        <div
                          className={clsx(
                            showDropDown && 'bg-backgroundGray',
                            'rounded-full   border hover:bg-grayZl-900 border-none  cursor-pointer  flex  justify-center items-center bg-cartBgColor',
                          )}
                        >
                          <div
                            style={{
                              boxShadow: '0px 4px 4px 0px #00000040',
                            }}
                            className='h-6 w-6  lg:w-8 lg:h-8 m-1 flex justify-center items-center rounded-full'
                          >
                            <RenderAvatar
                              wrapperStyle='rounded-full  '
                              width={screens.lg ? 30 : 24}
                              avatarStyle={{
                                fontSize: '10px',
                                border: '1px solid #FFFFFF0D',
                              }}
                              userName={userName}
                              avatarSrc={avatarSrc as string}
                              gradientBg={gradientBg}
                              setShowDropDown={setShowDropDown}
                              onClose={onClose}
                            />
                          </div>

                          <div className='flex h-6 w-6 lg:w-8 lg:h-8 ml-1 my-1 items-center justify-center cursor-pointer'>
                            <div className='relative'>
                              <MenuOutlined
                                className={clsx(
                                  'absolute text-fadedWhite text-xs -left-3.5 -top-1.5 md:text-sm transition-all duration-300',
                                  showDropDown
                                    ? 'opacity-0 rotate-90 scale-75'
                                    : 'opacity-100 rotate-0 scale-100',
                                )}
                              />
                              <CloseOutlined
                                className={clsx(
                                  'absolute text-fadedWhite text-xs -left-3.5 -top-1.5 md:text-sm transition-all duration-300',
                                  showDropDown
                                    ? 'opacity-100 rotate-0 scale-100'
                                    : 'opacity-0 -rotate-90 scale-75',
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </Space>
                    </div>
                  </Dropdown>
                </Badge> */}
              {/* </ShouldRender> */}

              {/* <ShouldRender check={!loggedIn && isCheckoutPage}>
                <span className='flex h-full items-center'>
                  <ButtonDefault
                    size={0}
                    rounded={false}
                    roundedSm={true}
                    variant={ButtonVariants.PRIMARY}
                    onClick={() =>
                      setVisible((prevState) => ({
                        ...prevState,
                        login: true,
                      }))
                    }
                    className='flex justify-center px-5   font-medium text-xs  whitespace-nowrap'
                  >
                    <span className='px-2 xxxs:px-3 lg:px-4 py-[8px] xxxs:py-[6px] lg:py-[9px] xl:py-[10px] text-xs xxxs:text-sm font-medium text-white'>
                      Sign In
                    </span>
                  </ButtonDefault>
                </span>
              </ShouldRender> */}
            </div>
          </nav>
          <ShouldRender
            check={
              validPathSearchNav.some((path) => path === router.pathname) &&
              !screens.lg
            }
          >
            <motion.div
              className={clsx(
                // eslint-disable-next-line no-constant-condition
                false
                  ? // isBannerVisibleValue
                    'md:translate-y-8 lg:translate-y-10 mt-[30px]'
                  : 'md:translate-y-8 lg:translate-y-10 mt-12',
                'flex bg-bgColor justify-between gap-1 z-0 md:gap-2 items-center pb-1 md:pb-2 md:mt-3 pt-3 px-4 md:px-5 lg:px-12',
                '',
              )}
            >
              <ShouldRender
                check={
                  router.asPath !== '/contact/' &&
                  !router.asPath.includes('/broadcast-message/') &&
                  !router.pathname.includes('/books/[bookId]') &&
                  validPathSearch.includes(router.pathname) &&
                  !screens.lg &&
                  isShopPage
                }
              >
                <></>
                {/* <div
                  className='flex justify-center items-center w-full'
                  style={{ minHeight: '48px' }}
                >
                  {router.isReady ? (
                    <Input
                      size='small'
                      ref={inputRef}
                      placeholder='Search books, courses, schools and live'
                      className='text-sm rounded-full w-full focus-within:bg-bgColor border-[1.5px]   placeholder:text-textDarkGrayColor py-1 px-3 text-white h-9 lg:h-10 bg-bgColor customInputBlack makeBgBlack'
                      value={searchKeyword}
                      style={{
                        backgroundColor: '#000000',
                      }}
                      onChange={handleInputChange}
                      prefix={
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2 }}
                          className=''
                        >
                          <SearchIconSVG width={screens.lg ? '20' : '16'} />
                        </motion.div>
                      }
                      suffix={
                        searchKeyword?.length && (
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <XIcon
                              onClick={() => {
                                Cookies.set('search', '');
                                setSearchKeyword('');
                                const { search, ...restQuery } = router.query;
                                router.push(
                                  {
                                    pathname: router.pathname,
                                    query: restQuery,
                                  },
                                  undefined,
                                  { shallow: true },
                                );
                                setShowSearch(false);
                              }}
                              className='w-5 h-5 pr-1 text-[#A6A6A6] cursor-pointer'
                            />
                          </motion.div>
                        )
                      }
                    />
                  ) : (
                    <div className='bg-cartBgColor h-10 w-full lg:max-w-[195px] rounded-full animate-pulse' />
                  )}
                </div> */}
              </ShouldRender>
              <ShouldRender check={isShopPage}>
                <></>
                {/* <motion.div
                  key='navbar'
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <AnimatedNavbar />
                </motion.div> */}
              </ShouldRender>
            </motion.div>
            <ShouldRender check={!screens.md}>
              <span className=' flex  bg-bgColor justify-between gap-1 md:gap-2 overflow-x-auto no-scrollbar items-center z-10 pb-2 pt-1 md:pb-4 md:mt-4  px-4 md:px-5 lg:px-12'>
                <ShouldRender
                  check={shouldRenderLinks && isCheckoutPage && !isCartPage}
                >
                  {links.map((link, index) => (
                    <div
                      key={`${link.route}-${index}`}
                      className='relative inline-block w-full'
                    >
                      <HeaderLink
                        text={link.text}
                        index={index}
                        open={openDropDown}
                        route={link.route}
                        notHaveDropDown={link?.notHaveDropDown || false}
                        setOpen={setOpen}
                      />

                      {openDropDown && hoveredIndex === index && link.type && (
                        <div className='absolute left-0 top-full z-50'>
                          <ProductDropdown
                            open={openDropDown}
                            setOpen={setOpenDropDown}
                            type={link.type}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </ShouldRender>
              </span>
            </ShouldRender>
          </ShouldRender>
        </div>
      </ConfigProvider>

      <Drawer
        className='drawerPaddingNone drawerPaddingNoScrollBar  h-full bg-grayZl-950'
        width={450}
        height='100%'
        classNames={{
          header: 'bg-black p-4 flex item border-0',
        }}
        title={
          <div className='flex items-center justify-between '>
            <span
              onClick={() => router.push('/')}
              className='cursor-pointer flex  md:pl-0 justify-start w-2/5 h-5'
            >
              <span>
                <Image
                  src={ZebraLearnLogo}
                  alt='Logo'
                  className='object-contain w-full h-full max-w-36'
                />
              </span>
            </span>
            <span
              className='flex justify-end'
              onClick={() => {
                onClose();
              }}
            >
              <XIcon className='text-fadedWhite w-4 h-4' />
            </span>
          </div>
        }
        placement='right'
        closeIcon={false}
        onClose={onClose}
        open={open}
      >
        <div className={interTight.className}>
          <div className='overflow-y-auto flex flex-col gap-4 '>
            <ViewProfile
              className='p-4'
              userName={userName}
              avatarSrc={avatarSrc}
              gradientBg={gradientBg}
              RenderAvatar={RenderAvatar}
              setShowDropDown={setShowDropDown}
              onClose={onClose}
              setOpen={setOpen}
              displayName={displayName}
            />
            <SubscribeButton
              setShowDropDown={setShowDropDown}
              setOpen={setOpen}
            />
            <PersonalityValue
              onClose={onClose}
              setShowDropDown={setShowDropDown}
            />
            {/* <CompleteProfile
              sendVerificationLink={sendVerificationLink}
              setShowDropDown={setShowDropDown}
              onClose={onClose}
            /> */}
            {/* <ReferEarn onClose={onClose} setShowDropDown={setShowDropDown} /> */}
            {renderMenuSection({
              items: [
                // ...(isSubscriptionActive(userData)
                //   ? [
                //       {
                //         text: 'Subscription',
                //         link: 'subscription',
                //         customSVG: <CrownOutlineIcon width={20} height={20} />,
                //         onClick: () => {
                //           router.push('/subscription/');
                //           onClose();
                //         },
                //       },
                //     ]
                //   : []),
                {
                  text: 'Library',
                  link: 'library',
                  customSVG: <LibraryIcon width={20} height={20} />,
                  onClick: () => {
                    router.push('/library/');
                    onClose();
                  },
                },
                {
                  text: 'Cart',
                  link: 'cart',
                  customSVG: <ShoppingCart width={20} />,
                  onClick: () => {
                    router.push('/cart');
                    onClose();
                  },
                },
                // {
                //   text: 'Rewards',
                //   link: 'reward-store',
                //   customSVG: <RewardIcon width={20} height={20} />,
                //   onClick: () => {
                //     router.push('/reward-store');
                //     onClose();
                //   },
                // },
                {
                  text: 'My Orders',
                  link: 'my-orders',
                  customSVG: <ReceiptItem width={20} height={20} />,
                  onClick: () => {
                    router.push('/my-orders/');
                    onClose();
                  },
                },
                // {
                //   text: 'Message',
                //   link: 'broadcast-message',
                //   customSVG: <ChatIcon width={20} height={20} />,
                //   onClick: () => {
                //     router.push('/broadcast-message');
                //     onClose();
                //   },
                // },
                {
                  text: 'Security & Passwords',
                  customSVG: <KeyIcon />,
                  onClick: () => {
                    router.push('/profile/?activeTab=2');
                    onClose();
                  },
                },
                {
                  text: 'Help & Support',
                  customSVG: <InfoCircleIcon width='20' />,
                  onClick: () => {
                    if (window !== undefined) {
                      window.open(
                        'https://support.zebralearn.com/support/home',
                        '_blank',
                      );
                    }
                    onClose();
                  },
                },
              ],
            })}
            <LogoutButton className='pb-0' setShowDropDown={setShowDropDown} />
          </div>
          <div className='flex flex-col gap-3 items-center px-4 py-8'>
            <p className='text-sm leading-[22px] font-medium text-white'>
              Download Our App
            </p>
            <div className='flex items-center gap-2 flex-wrap justify-center'>
              <PlayStoreGetIcon
                onClick={() => {
                  if (typeof window !== undefined) {
                    window.open(androidRedirectUrl as string, '_blank');
                  }
                }}
              />
              <AppStoreGetIcon
                onClick={() => {
                  if (typeof window !== undefined) {
                    window.open(
                      'https://apps.apple.com/in/app/zebralearn/id6477746268?utm_source=zebralearn_website',
                      '_blank',
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>
      <ShouldRender
        check={
          localCartCount &&
          !isCartPage &&
          isCheckoutPage &&
          router.pathname !== '/broadcast-message' &&
          !router.pathname.includes('/books/[bookId]') &&
          !router.pathname.includes('/courses/[courses_slug]') &&
          !router.pathname.includes('/reader-personality-test') &&
          !router.pathname.includes('/[watchId]') &&
          !isPdf
        }
      >
        <FloatingCartButton localCartCount={localCartCount} />
      </ShouldRender>
      <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        noRedirect={visible?.noRedirect}
      />
      <LoginModal onCancel={onCancelCloseLoginModal} visible={visible.login} />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
      />
      <GetOurAppModal
        visible={visibleGetAppModal}
        onCancel={() => setVisibleGetAppModal(false)}
        isClosable={true}
      />

      <VerifyEmailModal
        visible={visibleVerifyEmailModal}
        onCancel={() => setVisibleVerifyEmailModal(false)}
      />
    </>
  );
};

export default ProductsHeader;
